import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import type { SniperGroup } from '../../types/sniper.types';
import {
  deriveSniperGroupBadge,
  sniperGroupMarketCount,
} from '../../utils/sniperGroupState';
import { SniperGroupControls } from './SniperGroupControls';

/**
 * The Sniper groups landing — the first thing on `/predictions/sniper`. A LIST
 * of groups; each card shows the name, market count, a live state badge
 * (running+mode / stopped / panicked) and the per-group Start(test|normal) /
 * Stop / Panic controls. Clicking the card body opens the group's DETAIL.
 * Mirrors the opener `GroupPickerLanding` (a picker list, controls inline) and
 * shares the opener pages' centered empty-state treatment.
 */
export function SniperGroupsLanding({
  groups,
  loading,
  onSelectGroup,
  onDeleteGroup,
  onCreateGroup,
  deleteBusy,
}: {
  groups: SniperGroup[];
  loading?: boolean;
  onSelectGroup: (groupId: string) => void;
  onDeleteGroup: (group: SniperGroup) => void;
  onCreateGroup?: () => void;
  deleteBusy?: boolean;
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <GpsFixedIcon sx={{ color: 'grey.700', fontSize: 40, mb: 1 }} />
        <Typography sx={{ color: 'grey.400', fontSize: '0.95rem', mb: 0.5 }}>
          No Sniper groups yet
        </Typography>
        <Typography sx={{ color: 'grey.500', fontSize: '0.8rem', mb: 2 }}>
          Create a group to start adding matches and arming markets.
        </Typography>
        {onCreateGroup && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreateGroup}
            sx={{ fontSize: '0.7rem' }}
          >
            New group
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      {groups.map((g) => {
        const badge = deriveSniperGroupBadge(g);
        const markets = sniperGroupMarketCount(g);
        return (
          <Paper
            key={g._id}
            variant="outlined"
            sx={{
              p: 1.5,
              borderColor: badge.panicked
                ? 'error.main'
                : g.running
                  ? 'success.main'
                  : 'divider',
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() => onSelectGroup(g._id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onSelectGroup(g._id);
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'common.white', fontWeight: 600 }}
                >
                  {g.name}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${g.events?.length ?? 0} event(s) · ${markets} market(s)`}
                />
                <Chip
                  size="small"
                  color={badge.severity}
                  variant={badge.severity === 'default' ? 'outlined' : 'filled'}
                  label={badge.label}
                  sx={{ fontWeight: 700 }}
                />
                <ChevronRightIcon sx={{ color: 'grey.600' }} />
              </Box>

              <SniperGroupControls group={g} />

              <Tooltip title="Delete group (cascades markets)">
                <span>
                  <IconButton
                    onClick={() => onDeleteGroup(g)}
                    disabled={deleteBusy}
                    aria-label="delete group"
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
